import { captureRemixErrorBoundaryError } from '@sentry/remix';
import { lazy, Component, type ComponentProps, type ErrorInfo, type ReactNode } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { Header } from '~/components/header/Header';
import { LazyLoadWrapper, retry } from '~/components/ui/LazyLoadWrapper';
import { LightRays } from '~/components/ui/LightRays.client';
import { sendSentryError } from '~/lib/sentry.client';
import { classNames } from '~/utils/classNames';

interface Props {
  showHeading?: boolean;
  children?: ReactNode;
}

interface State {
  error?: string;
  componentStack?: ErrorInfo['componentStack'];
  isDiscordModalOpen: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    isDiscordModalOpen: false,
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      error: formatError(error),
      componentStack: errorInfo.componentStack,
    });

    const errorWithComponentStack = new Error(`${error.message}\nComponent stack: ${errorInfo.componentStack}`, {
      cause: error.cause,
    });

    errorWithComponentStack.stack = error.stack;
    errorWithComponentStack.name = 'BoltRenderError';
    captureRemixErrorBoundaryError(error);
    sendSentryError(errorWithComponentStack);
  }

  render() {
    if (this.state.error || this.state.componentStack) {
      return (
        <div
          className={`flex flex-col ${this.props.showHeading ? 'h-100vh' : 'h-[calc(100vh-var(--header-height))]'} bg-bolt-elements-background-depth-1`}
        >
          <DiscordSupportModal
            open={this.state.isDiscordModalOpen}
            onClose={() => this.setState({ isDiscordModalOpen: false })}
          />
          {this.props.showHeading && <Header />}

          <ClientOnly>{() => <LightRays />}</ClientOnly>

          <h1 className="text-[44px] text-center font-semibold text-bolt-elements-textPrimary tracking-tight my-5">
            An unexpected error occured
          </h1>

          <div className="flex flex-col overflow-auto m-10 mt-0 p-5 bg-bolt-elements-background-depth-2 border border-bolt-elements-borderColor rounded-xl shadow-md">
            <div className="flex flex-justify-between flex-items-center mb-5">
              <h2 className="text-base">Errors</h2>

              <IconButton
                size="sm"
                title="Copy error"
                icon="i-ph:copy"
                onClick={() => navigator.clipboard.writeText(this.getFormattedError())}
              />
            </div>

            {isCausedByBrowserExtension(this.state.error) && (
              <div className="text-sm flex items-center gap-4 p-4 mb-4 border border-bolt-elements-borderColor rounded-xl">
                <div className="bg-bolt-elements-item-backgroundWarn rounded-full w-12 h-12 p-3">
                  <span className="i-ph:warning shrink-0 inline-block h-full w-full color-bolt-elements-item-contentWarn" />
                </div>

                <div>
                  <p>This error was likely caused by your browser plugins.</p>
                  <p className="color-bolt-elements-textSecondary">
                    Please try disabling browser plugins that modify the webpage and try again.
                  </p>
                </div>
              </div>
            )}

            <ErrorMessage>{this.state.error}</ErrorMessage>
            <ErrorMessage className="mt-4">Component stack: {this.state.componentStack}</ErrorMessage>

            <div className="flex flex-justify-end gap-5 pt-5 mt-2 mx--5 px-5 border-t border-bolt-elements-borderColor">
              <Button variant="ghost" size="md" onClick={() => this.setState({ isDiscordModalOpen: true })}>
                Report an issue
              </Button>

              <Button
                size="md"
                onClick={() =>
                  this.setState({ error: undefined, componentStack: undefined, isDiscordModalOpen: false })
                }
              >
                Try again
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }

  getFormattedError() {
    return `${this.state.error}\nComponent stack: ${this.state.componentStack}`;
  }
}

function ErrorMessage(props: ComponentProps<'pre'>) {
  return (
    <pre
      {...props}
      translate="no"
      className={classNames(
        props.className,
        'flex-1 overflow-auto p-3',
        'rounded-md',
        'color-bolt-elements-item-contentDanger bg-bolt-elements-item-backgroundDanger',
      )}
    />
  );
}

function DiscordSupportModal(props: ComponentProps<typeof DiscordSupportModalLazyLoaded>) {
  return (
    <LazyLoadWrapper name="DiscordSupportModal" fallback={<div className="h-96" />}>
      <DiscordSupportModalLazyLoaded {...props} />
    </LazyLoadWrapper>
  );
}

const DiscordSupportModalLazyLoaded = lazy(
  retry(() =>
    import('~/components/chat/DiscordSupportModal').then((module) => ({
      default: module.DiscordSupportModal,
    })),
  ),
);

function formatError(error: Error) {
  let message = error.stack;

  if (!message?.includes(error.name)) {
    message = error.name + '\n' + message;
  }

  // e.g. Safari
  if (!message?.includes(error.message)) {
    message = error.message + '\n' + message;
  }

  return message;
}

const DOM_MODIFIED_PATTERN = new RegExp(
  [
    '(The object can not be found here)',
    '(Failed to execute .?removeChild)',
    '(node to be removed is not a child of this node)',
    '(node is to be inserted is not a child of this node)',
  ].join('|'),
);

function isCausedByBrowserExtension(error?: string) {
  return error && DOM_MODIFIED_PATTERN.test(error);
}
